/**
 * @ Copyright (C) 2022-present Highway 9 Networks.
 */

interface FillTimeSeriesDataOptions {
  data: number[][];
  startTime: number;
  endTime: number;
  interval?: number;
  modifier?: (val: any) => any;
  timeRoundingToNearest?: 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years' | number;
  fillType?:
    | "zero"
    | "average"
    | "last"
    | "first"
    | "previous"
    | "next"
    | "average-previous-next"
    | "max"
    | "min"
    | "none"
    | "null";
  name?: string;
}

/**
 *  This function is used to fill the time series data with 0 values
 * @param data - the data to be filled
 * @param startTime - the start time of the data
 * @param endTime - the end time of the data
 * @param interval - the interval of the data in seconds (default is 1 day)
 * @param modifier - a function to modify the value of the data (default is identity function)
 * @param fillType - the type of filling to be done (default is 'zero')
 * @param timeRoundingToNearest - the time rounding to the nearest value (default is seconds);
 * @param name - the name of the data
 * @returns the filled data with time series format (array of [time, value]) in milliseconds
 */

export function fillTimeSeriesData({
  data,
  startTime,
  endTime,
  interval = 60 * 60 * 24,
  modifier = (val: any) => val,
  fillType = "zero",
  timeRoundingToNearest = 'seconds',
  name = "",
}: FillTimeSeriesDataOptions): [number, number][] {
  const timeSeriesData: [number, number][] = [];

  // console.log("fillTimeSeriesData", name, "dataCount:", data.length, "startTime:", startTime, "endTime:", endTime, "interval:", interval, "modifier:", modifier, "fillType:", fillType);

  // safety check
  if (data.length === 0) {
    // Create points with null value to render the graph
    for (let i = startTime; i <= endTime; i += interval) {
      // @ts-ignore
      timeSeriesData.push([i * 1000, null]);
    }
    return timeSeriesData;
  }
  if (startTime > endTime) {
    console.log("name:", name, "startTime:", startTime, "endTime:", endTime);
    throw new Error("Start time is greater than end time");
  }
  if (interval <= 0) {
    console.log("name:", name, "interval:", interval);
    throw new Error("Interval must be greater than 0");
  }
  if (startTime > data[0][0]) {
      // Extend the window backwards to include earlier data, but snap the new
      // origin to whole-interval steps from the original startTime. Re-gridding
      // each series to its own data[0][0] gave every series a different origin,
      // so a percent series and its numerator/denominator series landed on
      // mismatched timestamps and the tooltip's lookup-by-timestamp missed,
      // showing 0/0. Keeping a shared, grid-aligned origin guarantees all series
      // produce identical timestamps. 
      startTime -= Math.ceil((startTime - data[0][0]) / interval) * interval;
  }
  if (endTime < data[data.length - 1][0]) {
    // Extend forwards while preserving the shared grid alignment.
    endTime += Math.ceil((data[data.length - 1][0] - endTime) / interval) * interval;
  }
  if (data[0][0] > data[data.length - 1][0]) {
    console.log("name:", name, "data[0][0]:", data[0][0], "data[data.length - 1][0]:", data[data.length - 1][0]);
    throw new Error("Data is not sorted");
  }
  if (data[0][0] === data[data.length - 1][0]) {
    // console.log("name:", name, "data[0][0]:", data[0][0], "data[data.length - 1][0]:", data[data.length - 1][0]);
    console.warn("Data has only one point : ", name);
  }

  // Process data points and organize by time interval
  const dataByInterval = new Map<number, number[]>();
  
  // First round all data timestamps according to the specified rounding
  const roundedData = data.map(([time, value]) => [roundTime(time, timeRoundingToNearest), value]);
  
  // Group data points by their time interval
  for (const [time, value] of roundedData) {
    // Calculate which interval bucket this data point belongs to
    const intervalBucket = Math.floor((time - startTime) / interval) * interval + startTime;
    
    if (!dataByInterval.has(intervalBucket)) {
      dataByInterval.set(intervalBucket, []);
    }
    
    dataByInterval.get(intervalBucket)?.push(value);
  }

  // Generate time series data for each interval
  for (let i = startTime; i <= endTime; i += interval) {
    const currentTime = i;
    const roundedTime = roundTime(currentTime, timeRoundingToNearest);
    let value: number | null = 0;

    // If we have data for this interval, use it
    if (dataByInterval.has(currentTime)) {
      const intervalValues = dataByInterval.get(currentTime) || [];
      
      // Use the average if multiple values exist for this interval
      if (intervalValues.length > 0) {
        // Filter out null/undefined values
        const validValues = intervalValues.filter(val => val !== null && val !== undefined);

        // If all values are null/undefined, return null
        if (validValues.length === 0) {
          value = null;
        } else {
          // Calculate average of valid values
          value = validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
        }
      }
    } else {
      // Apply the requested fill type for missing data points
      switch (fillType) {
        case "zero":
          value = 0;
          break;
        case "average":
          value = data.reduce((acc, val) => acc + val[1], 0) / data.length;
          break;
        case "last":
          value = data[data.length - 1][1];
          break;
        case "first":
          value = data[0][1];
          break;
        case "previous": {
          // Find the latest data point before this interval
          const previousIntervals = Array.from(dataByInterval.keys())
            .filter(time => time < currentTime)
            .sort((a, b) => b - a);
          
          if (previousIntervals.length > 0) {
            const previousValues = dataByInterval.get(previousIntervals[0]) || [];
            value = previousValues.length > 0 ? 
              previousValues.reduce((sum, val) => sum + val, 0) / previousValues.length : 0;
          } else {
            value = 0;
          }
          break;
        }
        case "next": {
          // Find the earliest data point after this interval
          const nextIntervals = Array.from(dataByInterval.keys())
            .filter(time => time > currentTime)
            .sort((a, b) => a - b);
          
          if (nextIntervals.length > 0) {
            const nextValues = dataByInterval.get(nextIntervals[0]) || [];
            value = nextValues.length > 0 ? 
              nextValues.reduce((sum, val) => sum + val, 0) / nextValues.length : 0;
          } else {
            value = 0;
          }
          break;
        }
        case "average-previous-next": {
          const previousIntervals = Array.from(dataByInterval.keys())
            .filter(time => time < currentTime)
            .sort((a, b) => b - a);
          
          const nextIntervals = Array.from(dataByInterval.keys())
            .filter(time => time > currentTime)
            .sort((a, b) => a - b);
          
          let previousValue = 0;
          let nextValue = 0;
          
          if (previousIntervals.length > 0) {
            const previousValues = dataByInterval.get(previousIntervals[0]) || [];
            previousValue = previousValues.length > 0 ? 
              previousValues.reduce((sum, val) => sum + val, 0) / previousValues.length : 0;
          }
          
          if (nextIntervals.length > 0) {
            const nextValues = dataByInterval.get(nextIntervals[0]) || [];
            nextValue = nextValues.length > 0 ? 
              nextValues.reduce((sum, val) => sum + val, 0) / nextValues.length : 0;
          }
          
          value = (previousValue + nextValue) / 2;
          break;
        }
        case "max":
          value = Math.max(...data.map((val) => val[1]));
          break;
        case "min":
          value = Math.min(...data.map((val) => val[1]));
          break;
        case "null":
          value = null;
          break;
        case "none":
          continue;
      }
    }

    timeSeriesData.push([roundedTime * 1000, modifier(value)]);
  }

  return timeSeriesData;
}

const roundTime = (time: number, timeRoundingToNearest: 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years' | number) => {
  if (timeRoundingToNearest === 'seconds') {
    return time;
  } else if (timeRoundingToNearest === 'minutes') {
    return Math.floor(time / 60) * 60;
  } else if (timeRoundingToNearest === 'hours') {
    return Math.floor(time / 3600) * 3600;
  } else if (timeRoundingToNearest === 'days') {
    return Math.floor(time / (3600 * 24)) * (3600 * 24);
  } else if (timeRoundingToNearest === 'weeks') {
    return Math.floor(time / (3600 * 24 * 7)) * (3600 * 24 * 7);
  } else if (timeRoundingToNearest === 'months') {
    return Math.floor(time / (3600 * 24 * 30)) * (3600 * 24 * 30);
  } else if (timeRoundingToNearest === 'years') {
    return Math.floor(time / (3600 * 24 * 365)) * (3600 * 24 * 365);
  } else {
    return Math.floor(time / timeRoundingToNearest) * timeRoundingToNearest;
  }
}

// // test cases
// let opts = {
//     data: [ [ 1620000000, 1 ], [ 1620000000, 2 ], [ 1620000000, 3 ] ],
//     startTime: 1620000000,
//     endTime: 1620000010,
//     interval: 1,
//     modifier: (val: any) => val * 10,
//     fillType: 'zero'
// }

// console.log("zero", fillTimeSeriesData({...opts, fillType: 'zero'}))
// console.log("average", fillTimeSeriesData({...opts, fillType: 'average'}))
// console.log("last", fillTimeSeriesData({...opts, fillType: 'last'}))
// console.log("first", fillTimeSeriesData({...opts, fillType: 'first'}))
// console.log("previous", fillTimeSeriesData({...opts, fillType: 'previous'}))
// console.log("next", fillTimeSeriesData({...opts, fillType: 'next'}))
// console.log("average-previous-next", fillTimeSeriesData({...opts, fillType: 'average-previous-next'}))
